import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faDollarSign, faShoppingCart, faStar, faHourglassHalf, faSync } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportsData.css';
import baseURL from '../../url';
import moneda from '../../moneda';

export default function ReportsData() {
    const [loading, setLoading] = useState(false);
    const [preset, setPreset] = useState('hoy');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    
    // Datos del reporte
    const [data, setData] = useState({
        metricas: {
            ventas_totales: 0,
            cambio_ventas: 0,
            pedidos_hoy: 0,
            cambio_pedidos_hoy: 0,
            ticket_promedio: 0,
            cambio_ticket: 0,
            pedidos_pendientes: 0,
            cambio_pendientes: 0
        },
        evolucion_ventas: [],
        top_productos: [],
        comparativo: {
            ingresos: 0,
            gastos: 0,
            serie_temporal: []
        },
        ultimos_pedidos: [],
        pedidos_por_estado: [],
        tipos_pedido: [],
        formas_pago: []
    });

    useEffect(() => {
        cargarReportes(true);

        // Auto-actualización cada 30 segundos en segundo plano
        const intervalId = setInterval(() => {
            cargarReportes(false);
        }, 30000);

        return () => clearInterval(intervalId);
    }, [preset, dateFrom, dateTo]);

    const cargarReportes = async (showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const response = await fetch(`${baseURL}/reportsGet.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    preset: preset,
                    date_from: dateFrom,
                    date_to: dateTo
                })
            });
            
            const result = await response.json();
            
            if (result.success) {
                setData(result);
            } else {
                if (showLoading) toast.error(result.message || 'Error al cargar reportes');
            }
        } catch (error) {
            console.error('Error:', error);
            if (showLoading) toast.error('Error al cargar los reportes');
        } finally {
            if (showLoading) setLoading(false);
        }
    };

    const handlePresetChange = (newPreset) => {
        setPreset(newPreset);
        setDateFrom('');
        setDateTo('');
    };

    const handleFilterApply = () => {
        cargarReportes();
    };

    const exportarExcel = async () => {
        try {
            const response = await fetch(`${baseURL}/reportsExport.php`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    preset: preset,
                    date_from: dateFrom,
                    date_to: dateTo
                })
            });

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = `reporte_${preset}_${Date.now()}.csv`;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
                toast.success('Reporte exportado exitosamente');
            } else {
                toast.error('Error al exportar reporte');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error al exportar el reporte');
        }
    };

    // Formatear porcentaje de cambio
    const formatearCambio = (cambio) => {
        const valor = parseFloat(cambio);
        if (valor === 0) return { texto: '0%', clase: '' };
        const signo = valor > 0 ? '+' : '';
        const clase = valor > 0 ? 'positive' : 'negative';
        return { texto: `${signo}${valor.toFixed(1)}%`, clase };
    };

    return (
        <div className="reportsContainer">
            
            
            {/* Header */}
            <div className="reportsHeader">
                <h2>Resumen del Negocio</h2>
                <button className="btnExportExcel" onClick={exportarExcel}>
                    <FontAwesomeIcon icon={faFileExcel} /> Exportar Datos
                </button>
            </div>

            {/* Filtros */}
            <div className="reportsFilters">
                <div className="filterPresets">
                    <span className="filterLabel">Seleccionar Periodo:</span>
                    <button 
                        className={preset === 'hoy' ? 'presetBtn active' : 'presetBtn'}
                        onClick={() => handlePresetChange('hoy')}
                    >
                        Hoy
                    </button>
                    <button 
                        className={preset === 'semana' ? 'presetBtn active' : 'presetBtn'}
                        onClick={() => handlePresetChange('semana')}
                    >
                        Esta Semana
                    </button>
                    <button 
                        className={preset === 'mes' ? 'presetBtn active' : 'presetBtn'}
                        onClick={() => handlePresetChange('mes')}
                    >
                        Este Mes
                    </button>
                    <button 
                        className={preset === 'ano' ? 'presetBtn active' : 'presetBtn'}
                        onClick={() => handlePresetChange('ano')}
                    >
                        Este Año
                    </button>
                    <button 
                        className={preset === 'personalizado' ? 'presetBtn active' : 'presetBtn'}
                        onClick={() => handlePresetChange('personalizado')}
                    >
                        Personalizado
                    </button>
                </div>

                {preset === 'personalizado' && (
                    <div className="customDateFilters">
                        <input 
                            type="date" 
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            placeholder="Desde"
                        />
                        <input 
                            type="date" 
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            placeholder="Hasta"
                        />
                    </div>
                )}

                <button className="btnRefresh" onClick={handleFilterApply} disabled={loading}>
                    <FontAwesomeIcon icon={faSync} spin={loading} /> Actualizar
                </button>
            </div>

            {loading && <div className="loadingOverlay">Cargando...</div>}

            {/* Métricas Principales */}
            <div className="metricsGrid">
                <div className="metricCard">
                    <div className="metricIcon ventas">
                        <FontAwesomeIcon icon={faDollarSign} />
                    </div>
                    <div className="metricContent">
                        <h3>{moneda} {data.metricas.ventas_totales.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ".")}</h3>
                        <p>Ventas Totales</p>
                        {(() => {
                            const cambio = formatearCambio(data.metricas.cambio_ventas);
                            return cambio.texto !== '0%' ? (
                                <span className={`metricChange ${cambio.clase}`}>{cambio.texto}</span>
                            ) : (
                                <span className="metricChange">Sin cambios</span>
                            );
                        })()}
                    </div>
                </div>

                <div className="metricCard">
                    <div className="metricIcon pedidos">
                        <FontAwesomeIcon icon={faShoppingCart} />
                    </div>
                    <div className="metricContent">
                        <h3>{data.metricas.pedidos_hoy}</h3>
                        <p>Pedidos de Hoy</p>
                        {(() => {
                            const cambio = formatearCambio(data.metricas.cambio_pedidos_hoy);
                            return cambio.texto !== '0%' ? (
                                <span className={`metricChange ${cambio.clase}`}>{cambio.texto}</span>
                            ) : (
                                <span className="metricChange">Sin cambios</span>
                            );
                        })()}
                    </div>
                </div>

                <div className="metricCard">
                    <div className="metricIcon rating">
                        <FontAwesomeIcon icon={faDollarSign} />
                    </div>
                    <div className="metricContent">
                        <h3>{moneda} {data.metricas.ticket_promedio.toFixed(2)}</h3>
                        <p>Ticket Promedio</p>
                        {(() => {
                            const cambio = formatearCambio(data.metricas.cambio_ticket);
                            return cambio.texto !== '0%' ? (
                                <span className={`metricChange ${cambio.clase}`}>{cambio.texto}</span>
                            ) : (
                                <span className="metricChange">Sin cambios</span>
                            );
                        })()}
                    </div>
                </div>

                <div className="metricCard">
                    <div className="metricIcon pendientes">
                        <FontAwesomeIcon icon={faHourglassHalf} />
                    </div>
                    <div className="metricContent">
                        <h3>{data.metricas.pedidos_pendientes}</h3>
                        <p>Pedidos Pendientes</p>
                        {(() => {
                            const cambio = formatearCambio(data.metricas.cambio_pendientes);
                            return cambio.texto !== '0%' ? (
                                <span className={`metricChange ${cambio.clase}`}>{cambio.texto}</span>
                            ) : (
                                <span className="metricChange">Sin cambios</span>
                            );
                        })()}
                    </div>
                </div>
            </div>



            {/* Segunda Fila de Gráficos */}
            <div className="chartsRowTwo">
                {/* Top 5 Productos */}
                <div className="chartCard">
                    <div className="chartHeader">
                        <h3>Top 5 Productos Vendidos</h3>
                    </div>
                    <div className="chartContent">
                        {data.top_productos.length > 0 ? (
                            <div className="donutChart">
                                <div className="donutCircle">
                                    <svg viewBox="0 0 100 100" className="donutSvg">
                                        {data.top_productos.map((item, index) => {
                                            const total = data.top_productos.reduce((sum, p) => sum + parseFloat(p.cantidad_total), 0);
                                            const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
                                            const percentage = (parseFloat(item.cantidad_total) / total) * 100;
                                            
                                            let cumulativePercentage = 0;
                                            for (let i = 0; i < index; i++) {
                                                cumulativePercentage += (parseFloat(data.top_productos[i].cantidad_total) / total) * 100;
                                            }
                                            
                                            const rotation = (cumulativePercentage / 100) * 360;
                                            const dashArray = `${percentage} ${100 - percentage}`;
                                            
                                            return (
                                                <circle
                                                    key={index}
                                                    cx="50"
                                                    cy="50"
                                                    r="15.915"
                                                    fill="transparent"
                                                    stroke={colors[index]}
                                                    strokeWidth="10"
                                                    strokeDasharray={dashArray}
                                                    strokeDashoffset="25"
                                                    transform={`rotate(${rotation - 90} 50 50)`}
                                                    className="donutSegment"
                                                    data-cantidad={parseFloat(item.cantidad_total)}
                                                    data-producto={item.producto_nombre}
                                                />
                                            );
                                        })}
                                    </svg>
                                    <div className="donutCenter">
                                        <span className="donutTotal">{data.top_productos.reduce((sum, p) => sum + parseFloat(p.cantidad_total), 0)}</span>
                                        <span className="donutLabel">unidades</span>
                                    </div>
                                    <div className="donutTooltip" style={{ display: 'none' }}>
                                        <span className="tooltipProducto"></span>
                                        <span className="tooltipCantidad"></span>
                                    </div>
                                </div>
                                <div className="donutLegend">
                                    {data.top_productos.map((item, index) => {
                                        const colors = ['#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF'];
                                        const total = data.top_productos.reduce((sum, p) => sum + parseFloat(p.cantidad_total), 0);
                                        const cantidad = parseFloat(item.cantidad_total);
                                        return (
                                            <div 
                                                key={index} 
                                                className="legendItem"
                                                onMouseEnter={(e) => {
                                                    const tooltip = e.currentTarget.parentElement.previousElementSibling.querySelector('.donutTooltip');
                                                    if (tooltip) {
                                                        tooltip.style.display = 'block';
                                                        tooltip.querySelector('.tooltipProducto').textContent = item.producto_nombre;
                                                        tooltip.querySelector('.tooltipCantidad').textContent = `${cantidad} unidades`;
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    const tooltip = e.currentTarget.parentElement.previousElementSibling.querySelector('.donutTooltip');
                                                    if (tooltip) {
                                                        tooltip.style.display = 'none';
                                                    }
                                                }}
                                            >
                                                <span className="legendColor" style={{ backgroundColor: colors[index] }}></span>
                                                <span className="legendText">{item.producto_nombre} ({cantidad})</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <p className="noData">No hay datos para mostrar</p>
                        )}
                    </div>
                </div>

                {/* Pedidos por Estado */}
                <div className="chartCard">
                    <div className="chartHeader">
                        <h3>Pedidos por Estado</h3>
                    </div>
                    <div className="chartContent">
                        {data.pedidos_por_estado.length > 0 ? (
                            <div className="estadosPedidoChart">
                                {data.pedidos_por_estado.map((estado, index) => {
                                    const total = data.pedidos_por_estado.reduce((sum, e) => sum + parseInt(e.cantidad), 0);
                                    const porcentaje = total > 0 ? ((parseInt(estado.cantidad) / total) * 100).toFixed(1) : 0;
                                    const colorsEstado = {
                                        'Pendiente': '#FF9800',
                                        'Pagado': '#4CAF50',
                                        'Entregado': '#2196F3',
                                        'Rechazado': '#f44336',
                                        'Cancelado': '#9E9E9E'
                                    };
                                    const color = colorsEstado[estado.estado] || '#757575';
                                    
                                    return (
                                        <div key={index} className="estadoPedidoItem">
                                            <div className="estadoPedidoLabel">
                                                <span className="estadoIndicator" style={{ backgroundColor: color }}></span>
                                                <span>{estado.estado}</span>
                                            </div>
                                            <div className="estadoPedidoBar">
                                                <div 
                                                    className="estadoPedidoFill"
                                                    style={{ 
                                                        width: `${porcentaje}%`,
                                                        backgroundColor: color
                                                    }}
                                                    title={`${moneda} ${parseFloat(estado.total_monto).toFixed(2)}`}
                                                ></div>
                                            </div>
                                            <div className="estadoPedidoValor">
                                                {estado.cantidad} ({porcentaje}%) - {moneda}{parseFloat(estado.total_monto).toFixed(2)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="noData">No hay datos para mostrar</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Comparativo y Últimos Pedidos */}
            <div className="chartsRow">
                {/* Comparativo Ingresos vs Gastos */}
                <div className="chartCard">
                    <div className="chartHeader">
                        <h3>Comparativa: Ingresos vs. Gastos</h3>
                    </div>
                    <div className="chartContent">
                        <div className="areaChart">
                            <div className="areaChartContent">
                                {/* Versión simplificada: mostrar totales */}
                                <div className="comparativoSimple">
                                    <div className="comparativoItem ingresos">
                                        <div className="comparativoLabel">Ingresos</div>
                                        <div className="comparativoBar">
                                            <div 
                                                className="comparativoFill"
                                                style={{ 
                                                    width: `${(data.comparativo.ingresos / (data.comparativo.ingresos + data.comparativo.gastos)) * 100}%` 
                                                }}
                                            ></div>
                                        </div>
                                        <div className="comparativoValue">{moneda} {data.comparativo.ingresos.toFixed(2)}</div>
                                    </div>
                                    <div className="comparativoItem gastos">
                                        <div className="comparativoLabel">Gastos</div>
                                        <div className="comparativoBar">
                                            <div 
                                                className="comparativoFill"
                                                style={{ 
                                                    width: `${(data.comparativo.gastos / (data.comparativo.ingresos + data.comparativo.gastos)) * 100}%` 
                                                }}
                                            ></div>
                                        </div>
                                        <div className="comparativoValue">{moneda} {data.comparativo.gastos.toFixed(2)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Últimos Pedidos */}
                <div className="chartCard">
                    <div className="chartHeader">
                        <h3>Últimos Pedidos</h3>
                    </div>
                    <div className="chartContent">
                        <div className="tableWrapper">
                            <table className="pedidosTable">
                                <thead>
                                    <tr>
                                        <th>ID Pedido</th>
                                        <th>Total</th>
                                        <th>Estado</th>
                                        <th>Hora</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.ultimos_pedidos.slice(0, 10).map((pedido) => (
                                        <tr key={pedido.idPedido}>
                                            <td>#{pedido.idPedido}</td>
                                            <td className="totalCol">{moneda}{parseFloat(pedido.total).toFixed(2)}</td>
                                            <td>
                                                <span className={`estadoBadge ${pedido.estado.toLowerCase()}`}>
                                                    {pedido.estado}
                                                </span>
                                            </td>
                                            <td>{pedido.fecha.split(' ')[1]}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tablas de Análisis */}
            <div className="analysisRow">
                {/* Tipos de Pedido */}
                <div className="analysisCard">
                    <h3>Tipos de Pedido</h3>
                    <div className="tiposPedidoChart">
                        {data.tipos_pedido.map((tipo, index) => {
                            const total = data.tipos_pedido.reduce((sum, t) => sum + parseInt(t.cantidad), 0);
                            const porcentaje = total > 0 ? ((parseInt(tipo.cantidad) / total) * 100).toFixed(1) : 0;
                            const colors = ['', '#2196F3', '#FF9800', '#9C27B0'];
                            
                            return (
                                <div key={index} className="tipoPedidoItem">
                                    <div className="tipoPedidoLabel">
                                        <span className="tipoIndicator" style={{ backgroundColor: colors[index] }}></span>
                                        <span>{tipo.tipo_entrega}</span>
                                    </div>
                                    <div className="tipoPedidoBar">
                                        <div 
                                            className="tipoPedidoFill"
                                            style={{ 
                                                width: `${porcentaje}%`,
                                                backgroundColor: colors[index]
                                            }}
                                        ></div>
                                    </div>
                                    <div className="tipoPedidoValor">
                                        {tipo.cantidad} ({porcentaje}%)
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Formas de Pago */}
                <div className="analysisCard">
                    <h3>Formas de Pago</h3>
                    <div className="tableWrapper">
                        <table className="formasPagoTable">
                            <thead>
                                <tr>
                                    <th>Método</th>
                                    <th>Transacciones</th>
                                    <th>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.formas_pago.map((forma, index) => (
                                    <tr key={index}>
                                        <td>{forma.metodo_pago}</td>
                                        <td>{forma.cantidad}</td>
                                        <td className="totalCol">{moneda} {parseFloat(forma.total_monto).toFixed(2)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}

